import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    ViewEncapsulation,
    Injector,
} from '@angular/core';
import { select } from 'd3-selection';
import { zoomIdentity } from 'd3-zoom';

import { EcgChartComponent } from '../../ecg-chart/ecg-chart.component';
import { Zoomer } from '../../utils/zoomer';
import { Layout } from '../../layout/layout';
import { LayerViewModel } from '../../view-models/layer-viewmodel';
import { Actions, ofType } from '@ngrx/effects';
import { ecgViewerActions } from 'projects/ecg-viewer-html5/src/store/actions';
import { Subscription } from 'rxjs';

@Component({
    selector: '[layers-centering]',
    templateUrl: './layers-centering.component.html',
    styleUrls: ['./layers-centering.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
})
export class LayersCenteringComponent implements OnDestroy {
    @Input()
    layers!: Array<LayerViewModel>;

    @Input()
    svgRoot!: any;

    visible: boolean = false;

    @Input()
    hideWhileCentering: boolean = true;

    @HostBinding('attr.transform')
    translate: string = '';
    subscriptions: Subscription[] = [];
    private layersCentering;
    private chart;
    private _ecgChart: EcgChartComponent | null = null;

    constructor(
        private injector: Injector,
        private layout: Layout,
        private zoomer: Zoomer,
        private element: ElementRef,
        private actions: Actions,
    ) {
        this.layersCentering = select(this.element.nativeElement);

        this.layout.layoutChange.subscribe((_) => {
            this.centerLayers();
        });
        this.subscriptions.push(
            this.actions.pipe(ofType(ecgViewerActions.fitMessage)).subscribe(() => {
                this.centerLayers();
            }),
        );
    }

    private get ecgChart(): EcgChartComponent | null {
        if (!this._ecgChart) {
            try {
                this._ecgChart = this.injector.get(EcgChartComponent, null);
                if (this._ecgChart) {
                    this.chart = select(this._ecgChart.element.nativeElement);
                }
            } catch (e) {
                return null;
            }
        }
        return this._ecgChart;
    }

    get isActiveLayerValid() {
        return this.ecgChart && this.ecgChart.chart && this.ecgChart.chart.activeLayer;
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    @HostListener('window:resize')
    onResize() {
        this.centerLayers();
    }

    centerLayers() {
        if (this.hideWhileCentering) this.visible = false;

        setTimeout(() => {
            this.translateToCenter();
            this.fit();

            this.visible = true;
        });
    }

    private translateToCenter() {
        if (!this.ecgChart) return;

        let chartBounds = {
            width: this.ecgChart.element.nativeElement.clientWidth,
            height: this.ecgChart.element.nativeElement.clientHeight,
        };

        let layersBounds = this.layersCentering.node().getBBox();

        let translateToCenterX = -layersBounds.x + (chartBounds.width / 2 - layersBounds.width / 2);
        let translateToCenterY = -layersBounds.y + (chartBounds.height / 2 - layersBounds.height / 2);

        this.translate =
            'translate(' + translateToCenterX + ', ' + Math.ceil((translateToCenterY / 5000) * 5000).toString() + ')';
    }

    private fit() {
        if (!this.ecgChart) return;

        let width = this.ecgChart.element.nativeElement.clientWidth;
        let height = this.ecgChart.element.nativeElement.clientHeight;
        let layersBounds = this.layersCentering.node().getBBox();

        const bounds = layersBounds;
        const dx = bounds.width;
        const dy = bounds.height;
        const scale = 0.95 * Math.max(1, Math.min(10, 1 / Math.max(dx / width, dy / height)));

        let newCenter = [(scale * width) / 2 - width / 2, (scale * height) / 2 - height / 2];
        let translate = [-newCenter[0], -newCenter[1]];

        this.zoomer.transform(zoomIdentity.translate(translate[0], translate[1]).scale(scale));
    }
}
